import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import {
  Bookmark,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Send,
} from 'lucide-react'
import { SplitKind, type SplitSettings } from '@/core/document'
import { TEST_IDS } from '@/lib/testIds'
import { cn } from '@/lib/utils'
import { PreviewCanvas } from '@/tools/preview/PreviewCanvas'
import type { PreviewTile } from '@/tools/preview/renderPreviewTiles'

interface PostPreviewProps {
  tiles: readonly PreviewTile[]
  split: SplitSettings | null
}

const SWIPE_THRESHOLD_CSS_PX = 36
const INSTAGRAM_MIN_FEED_RATIO = 4 / 5
const INSTAGRAM_MAX_FEED_RATIO = 1.91

function validatePostTiles(tiles: readonly PreviewTile[], split: SplitSettings | null): void {
  if (!split && (tiles.length !== 1 || tiles[0]?.slides.length !== 1)) {
    throw new Error('Single post preview requires exactly one upload with one image')
  }
  if (split?.kind === SplitKind.Carousel
    && (tiles.length !== 1 || tiles[0]?.slides.length !== split.tiles)) {
    throw new Error(`Carousel post preview requires one upload with ${split.tiles} slides`)
  }
  if (split?.kind === SplitKind.Grid && tiles.length !== split.rows * 3) {
    throw new Error(`Grid post preview requires ${split.rows * 3} uploads`)
  }
}

export function PostPreview({ tiles, split }: PostPreviewProps) {
  validatePostTiles(tiles, split)
  const [selectedUploadIndex, setSelectedUploadIndex] = useState(0)
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0)
  const swipeStartX = useRef<number | null>(null)
  const carousel = split?.kind === SplitKind.Carousel
  const grid = split?.kind === SplitKind.Grid
  const selectedTile = tiles[selectedUploadIndex]
  if (!selectedTile) throw new Error(`Post preview upload ${selectedUploadIndex + 1} is missing`)
  const slides = carousel ? selectedTile.slides : [selectedTile.postCanvas]
  const selectedSlide = slides[selectedSlideIndex]
  if (!selectedSlide) throw new Error(`Post preview slide ${selectedSlideIndex + 1} is missing`)
  const sourceRatio = selectedSlide.width / selectedSlide.height
  const feedRatio = Math.min(INSTAGRAM_MAX_FEED_RATIO, Math.max(INSTAGRAM_MIN_FEED_RATIO, sourceRatio))

  const selectUpload = (index: number) => {
    if (!tiles[index]) throw new Error(`Cannot select missing post preview upload ${index + 1}`)
    setSelectedUploadIndex(index)
    setSelectedSlideIndex(0)
  }

  const finishSwipe = (event: ReactPointerEvent<HTMLDivElement>) => {
    const startX = swipeStartX.current
    swipeStartX.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (startX === null || !carousel) return
    const deltaX = event.clientX - startX
    if (deltaX <= -SWIPE_THRESHOLD_CSS_PX && selectedSlideIndex < slides.length - 1) {
      setSelectedSlideIndex((current) => current + 1)
    } else if (deltaX >= SWIPE_THRESHOLD_CSS_PX && selectedSlideIndex > 0) {
      setSelectedSlideIndex((current) => current - 1)
    }
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-muted/30 p-3" data-testid={TEST_IDS.previewPost}>
      <div className="mx-auto w-full max-w-[390px]">
        {grid ? (
          <fieldset className="mb-3 rounded-lg border bg-background p-3">
            <legend className="px-1 text-xs font-medium">Choose uploaded post</legend>
            <div className="flex flex-wrap gap-1.5">
              {tiles.map((tile, index) => (
                <button
                  key={tile.uploadNumber}
                  type="button"
                  aria-pressed={selectedUploadIndex === index}
                  data-testid={TEST_IDS.previewPostPicker(tile.uploadNumber)}
                  onClick={() => selectUpload(index)}
                  className="size-8 rounded-md border text-xs font-semibold text-muted-foreground aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:text-background"
                >
                  {tile.uploadNumber}
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}

        <article className="overflow-hidden rounded-lg border bg-background shadow-sm" aria-label="Instagram post preview">
          <header className="flex h-12 items-center gap-2.5 px-3">
            <div className="size-8 rounded-full bg-gradient-to-br from-muted-foreground/30 to-muted" aria-hidden="true" />
            <span className="flex-1 text-sm font-semibold">your_name</span>
            <MoreHorizontal className="size-5" aria-label="More options" />
          </header>

          <div
            className={cn('relative flex w-full touch-pan-y items-center justify-center overflow-hidden bg-black', carousel && 'cursor-grab active:cursor-grabbing')}
            style={{ aspectRatio: String(feedRatio) }}
            data-testid={TEST_IDS.previewPostMedia}
            onPointerDown={(event) => {
              if (!carousel) return
              swipeStartX.current = event.clientX
              event.currentTarget.setPointerCapture(event.pointerId)
            }}
            onPointerUp={finishSwipe}
            onPointerCancel={(event) => {
              swipeStartX.current = null
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId)
              }
            }}
          >
            <PreviewCanvas
              source={selectedSlide}
              sourceRect={{ x: 0, y: 0, width: selectedSlide.width, height: selectedSlide.height }}
              outputSize={{ width: selectedSlide.width, height: selectedSlide.height }}
              className="block max-h-full max-w-full object-contain"
              ariaLabel={carousel
                ? `Carousel slide ${selectedSlideIndex + 1} of ${slides.length}`
                : `Post upload ${selectedTile.uploadNumber}`}
            />
            {carousel ? (
              <span
                className="absolute right-3 top-3 rounded-full bg-black/70 px-2 py-1 text-xs font-semibold text-white"
                data-testid={TEST_IDS.previewCarouselBadge}
              >
                {selectedSlideIndex + 1}/{slides.length}
              </span>
            ) : null}
          </div>

          <div className="px-3 pb-4 pt-3">
            <div className="flex items-center gap-4" aria-hidden="true">
              <Heart className="size-6" />
              <MessageCircle className="size-6" />
              <Send className="size-6" />
              {carousel ? (
                <div className="flex flex-1 justify-center gap-1.5" aria-label={`Carousel slide ${selectedSlideIndex + 1} of ${slides.length}`}>
                  {slides.map((_, index) => (
                    <span
                      key={index}
                      className={cn('size-1.5 rounded-full bg-muted-foreground/35', index === selectedSlideIndex && 'bg-blue-500')}
                    />
                  ))}
                </div>
              ) : <span className="flex-1" />}
              <Bookmark className="size-6" />
            </div>
            <p className="mt-3 text-sm"><strong>your_name</strong> Your caption will appear here…</p>
          </div>
        </article>
      </div>
    </div>
  )
}
